/**
 * Client-only synthetic column keys — column keys that live in a mount's
 * in-memory `working`/`baseline` columns list (so the dirty diff and column
 * order treat them like any other column) but are NOT a field either
 * server-side catalog (`@shared/flow/filter-fields`) knows about, because
 * their cell content is derived entirely client-side.
 *
 * Regression fixed here (live browser QA + HAR capture, 2026-08-12): saving
 * essentially every Contact-entity view 400'd with `columns: unknown field
 * "cross_app_state"`. Root cause — ContactFlowTable (contact/ContactFlowTable.tsx)
 * unconditionally inserts CROSS_APP_STATE_KEY (the "Terra Actions" pill-strip
 * column) into every opened view's columns via `adaptOnOpen`, and that list
 * rode straight through `definitionOf()` (useViewStateMachine.ts) into the
 * saved-view create/update body, which the write-time validator
 * (`validateViewFieldReferences`, consumed by `src/api/flow-views.ts`)
 * correctly rejects — it is not, and must never become, a real field.
 *
 * ONE list, so a future synthetic column (a different entity's own
 * client-derived display column) only needs to be added here to be safe by
 * construction, rather than every wire-serialization call site re-deriving
 * its own "is this a real field" test.
 *
 * Stripped ONLY at the saved-view definition boundary (`definitionOf`, used
 * by `save()`/`saveAsNew()`) — deliberately NOT at the working-state PUT
 * boundary (`useFlowViewStatePersist` in useViewState.ts). That endpoint's
 * schema (`FlowWorkingStateSchema`) has no field-catalog validation, so a
 * synthetic key round-trips it harmlessly, and NOT stripping it there is
 * what keeps a restored `working` state consistent with a mount-adapted
 * `baseline` (both carry it, so restoring never produces a phantom
 * "Modified" banner — see ContactFlowTable's `adaptOnOpen`, which injects
 * the key into EVERY opened view, preset or saved, for exactly this
 * consistency reason).
 *
 * There is exactly ONE synthetic column today: `CROSS_APP_STATE_KEY` (the
 * "Terra Actions" pill strip, from the batched POST /api/flow/contacts/state
 * read). Two others used to be listed here and both left the same way, by
 * becoming REAL catalog fields on the contact row: `SIGNAL_STATUS_KEY` (plan
 * 2026-09-09-003 U3a, the Plays mount's client-derived status cell; real
 * since plan 2026-09-10-001 D3/D10) and `USAGE_ROLE_KEY` (the "Clay Role"
 * chips, briefly client-derived from `ContactHeader.usageRoles`; real since
 * 2026-09-25, when `usage_roles` became a row field folded from the three
 * `is_*` SQL booleans that make roles filterable, `@shared/flow/contact-view`).
 * Stripping either here now would silently delete a rep's column on every
 * Save, so neither may be re-added.
 *
 * `priority`/`local_time` — the Signals tab's Ranker priority number and
 * business-hours local-time badge (`SIGNAL_SYNTHETIC_COLUMN_KEYS`,
 * `@shared/flow/signal-view`) — are deliberately NOT here, despite being
 * computed in the browser. Bead .13 added them on the assumption that the
 * write-time validator would reject them; it does not. Both are real
 * `SIGNAL_FIELDS` catalog entries, so `visibleColumnKeys('signal')`
 * (src/shared/flow/filter-fields.ts) contains them and
 * `validateViewFieldReferences` accepts them in a saved view's `columns` —
 * and `syntheticSortKeysFor('signal')` accepts them as a `sort` field too.
 *
 * Stripping them therefore did the exact damage this Set exists to prevent
 * (round-1 fix R10 / D-F): the server's response replaces working state, so
 * saving a Signals view silently deleted the rep's Priority and Local time
 * columns, and with no `adaptOnOpen` re-inserting them they never came back.
 *
 * The test for "is this key synthetic?" is NOT "is it computed client-side"
 * — it is "would the catalog reject it". `SIGNAL_SYNTHETIC_COLUMN_KEYS`
 * answers a different question (which keys the LIST endpoint must not
 * receive as `sort`/`group_by`, since no SQL column backs them) and is not
 * interchangeable with this one.
 */
export const CROSS_APP_STATE_KEY = 'cross_app_state';

const SYNTHETIC_COLUMN_KEYS: ReadonlySet<string> = new Set([CROSS_APP_STATE_KEY]);

/** Drop every synthetic column key from an ordered columns list, preserving order. */
export function stripSyntheticColumns(columns: string[]): string[] {
  return columns.filter(key => !SYNTHETIC_COLUMN_KEYS.has(key));
}
